import type { FC } from 'react'

import { useAppContext } from '@/App.context'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import Usage from '@/components/util/Usage'
import useSetup from '@/hooks/setup'

import { Translation } from '../i18n/Translation'

import { analyzePost } from './util'

import './style/salary-deduction-list-item.scss'

type Post = {
  amount: number
  free_allowance_participant: boolean
  from_period: string
  id: number
  is_locked: boolean
  off_peak_volume: number
  p_id: number
  peak_volume: number
  pending_salary_deduction_amount: number
  pending_salary_deduction_vat: number
  price_type: string
  private: boolean
  product: string
  quantity: number
  salary_deduction_amount: number
  salary_deduction_vat: number
  sda_amount: number
  sda_comment: string
  sda_id: number
  sda_status: number
  sda_vat: number
  sda_version: number
  to_period: string
  vat: number
}

type Application = {
  applicable: boolean
  code: number
  color: number
  desc: string
  header: string
  text: string
  warning: string | null
}

const SalaryDeductionListItem: FC<{
  post: Post
  isHeaderVisible: boolean
  headerNumber: number
  freeAllowanceLock: number
  deadLinePassed: boolean
  hasDeduction: boolean
  setUpApplication: (invoiceRow: unknown) => void
}> = ({
  post,
  headerNumber,
  freeAllowanceLock,
  deadLinePassed,
  hasDeduction,
  isHeaderVisible,
  setUpApplication,
}) => {
  const setup = useSetup()

  const application = analyzePost(
    post,
    freeAllowanceLock,
    hasDeduction,
    setup?.private_active,
    deadLinePassed
  )
  const { whiteLabel } = useAppContext()

  return (
    <div className={'post type' + application.color} key={post.id}>
      <Header
        isHeaderVisible={isHeaderVisible}
        headerNumber={headerNumber}
        application={application}
        className="type_header is-visible"
      />

      <div
        role="button"
        tabIndex={0}
        className="list_item is-visible"
        onClick={() => setUpApplication(post)}
        onKeyDown={() => setUpApplication(post)}
      >
        <div className={'color color' + application.color + ' ' + whiteLabel} />

        <div className="sda">
          <FormattedNumberWithCurrency
            amount={post.salary_deduction_amount + post.salary_deduction_vat}
            precision={2}
          />
        </div>

        <div className="desc">
          <Translation id={application.desc} />
        </div>

        <div className="prod">{post.product}</div>

        <Usage item={post} />
      </div>
    </div>
  )
}

const Header: FC<{
  isHeaderVisible: boolean
  headerNumber: number
  application: Application
  className: string
}> = ({ isHeaderVisible, headerNumber, application, className }) => {
  if (!isHeaderVisible) return null

  let headerClassName = className

  if (headerNumber === 1) {
    headerClassName += ' first'
  }

  return (
    <div className={headerClassName}>
      <Translation id={application.header} />
    </div>
  )
}

export default SalaryDeductionListItem
